<template>
  <textarea
    ref="redactorElement"
    :name="name"
    :placeholder="placeholder"
    :value="modelValue"
  />
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from "vue";

// Import Redactor library and styles
import "../admin/redactor/redactor.js";
import "../admin/redactor/redactor.css";

const props = defineProps({
  modelValue: { type: String, default: "" },
  placeholder: { type: String, default: null },
  name: { type: String, default: null },
  config: { type: Object, default: () => ({}) },
});

const emit = defineEmits(["update:modelValue"]);

const redactorElement = ref(null);
let redactorInstance = null;

watch(
  () => props.modelValue,
  (newValue) => {
    if (!redactorInstance) return;

    // Don't update if the editor has focus or is in source mode
    if (redactorInstance.editor.hasFocus() || redactorInstance.source.is()) {
      return;
    }

    redactorInstance.editor.setContent({ html: newValue });
  }
);

const initRedactor = () => {
  if (!redactorElement.value) return;

  // Defaults for plain HTML with no automatic wrappers
  const defaultConfig = {
    breakline: true,
    paste: {
      clean: true,
      autoparse: false,
      blockTags: [
        // Disallow all block tags on paste
      ],
      inlineTags: ["a", "br", "strong", "em", "i"],
      paragraphizeLines: false,
      keepEmptyLines: false,
    },
  };

  // Merge with defaults (deep for `paste`)
  const baseConfig = {
    content: props.modelValue,
    ...props.config,
  };
  const config = {
    ...baseConfig,
    breakline: baseConfig.breakline ?? true,
    paste: { ...defaultConfig.paste, ...(baseConfig.paste || {}) },
  };

  // Set up subscriptions
  config.subscribe = config.subscribe || {};

  // Sanitize: remove any trailing <br> tags at the very end
  const originalBeforeChange = config.subscribe["editor.before.change"];
  config.subscribe["editor.before.change"] = function (event) {
    try {
      let html = event.get("html");
      html = html.replace(/(?:<br\s*\/?>(?:\s|&nbsp;)*?)+$/gi, "");
      event.set("html", html);
    } catch (e) {
      // no-op
    }
    if (originalBeforeChange) originalBeforeChange.call(this, event);
  };

  // Also sanitize during HTML-string pipeline events
  const stripTrailingBrs = (html) =>
    typeof html === "string"
      ? html.replace(/(?:<br\s*\/?>(?:\s|&nbsp;|\u00A0)*)+$/gi, "")
      : html;

  const originalBeforeParse = config.subscribe["editor.before.parse"];
  config.subscribe["editor.before.parse"] = function (html) {
    const cleaned = stripTrailingBrs(html);
    const res = originalBeforeParse
      ? originalBeforeParse.call(this, cleaned)
      : undefined;
    return res !== undefined ? res : cleaned;
  };

  const originalParse = config.subscribe["editor.parse"];
  config.subscribe["editor.parse"] = function (html) {
    const cleaned = stripTrailingBrs(html);
    const res = originalParse ? originalParse.call(this, cleaned) : undefined;
    return res !== undefined ? res : cleaned;
  };

  const originalBeforeUnparse = config.subscribe["editor.before.unparse"];
  config.subscribe["editor.before.unparse"] = function (html) {
    const cleaned = stripTrailingBrs(html);
    const res = originalBeforeUnparse
      ? originalBeforeUnparse.call(this, cleaned)
      : undefined;
    return res !== undefined ? res : cleaned;
  };

  const originalUnparse = config.subscribe["editor.unparse"];
  config.subscribe["editor.unparse"] = function (html) {
    const cleaned = stripTrailingBrs(html);
    const res = originalUnparse
      ? originalUnparse.call(this, cleaned)
      : undefined;
    return res !== undefined ? res : cleaned;
  };

  const originalBeforeClean = config.subscribe["editor.before.clean"];
  config.subscribe["editor.before.clean"] = function (html) {
    const cleaned = stripTrailingBrs(html);
    const res = originalBeforeClean
      ? originalBeforeClean.call(this, cleaned)
      : undefined;
    return res !== undefined ? res : cleaned;
  };

  const originalClean = config.subscribe["editor.clean"];
  config.subscribe["editor.clean"] = function (html) {
    const cleaned = stripTrailingBrs(html);
    const res = originalClean ? originalClean.call(this, cleaned) : undefined;
    return res !== undefined ? res : cleaned;
  };

  // v-model sync on change
  const originalChange = config.subscribe["editor.change"];
  config.subscribe["editor.change"] = function (event) {
    try {
      const html = event.get ? event.get("html") : event.data?.html;
      if (html !== undefined) emit("update:modelValue", html);
    } catch (e) {
      // no-op
    }
    if (originalChange) originalChange.call(this, event);
  };

  // Initialize Redactor
  try {
    redactorInstance = window.Redactor(redactorElement.value, config);
  } catch (error) {
    console.error("Failed to initialize Redactor:", error);
  }
};

const destroyRedactor = () => {
  if (redactorInstance) {
    try {
      redactorInstance.destroy();
    } catch (error) {
      console.error("Failed to destroy Redactor:", error);
    } finally {
      redactorInstance = null;
    }
  }
};

onMounted(() => {
  if (typeof window.Redactor === "undefined") {
    console.error("Redactor library is not loaded");
    return;
  }

  // Use nextTick to ensure DOM is ready
  setTimeout(() => {
    initRedactor();
  }, 0);
});

onBeforeUnmount(() => {
  destroyRedactor();
});
</script>
